import React, {useEffect, useMemo, useState} from 'react';
import {VStack} from 'native-base';
import {DndContext, closestCenter, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {arrayMove, SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import uuid from 'react-native-uuid';
import * as yup from 'yup';
import {t} from '../../../../../../services/i18n';
import {fetchApi} from '../../../../../../services/HTTP';
import CustomButtonV2 from '../../../../../common/CustomButtonV2';
import {Colors} from '../../../../../../styles/Colors';
import UpsellRow from './UpsellRow';

export const mapUpsellInitialRows = (upsell) => {
    if (!upsell?.length) return [];
    return upsell.map((entry, index) => ({
        membership_types_id: entry.membership_types_id ?? null,
        price: entry.price ?? null,
        order: entry.order ?? index,
        originalPrice: entry.membership_types?.price ?? entry.originalPrice ?? null,
    }));
};

export const getUpsellValidationSchema = () => yup.array().of(yup.object().shape({
    membership_types_id: yup.number().nullable(),
    price: yup.number().nullable()
        .test('upsell-price-range', t('upsell-price-range-error'), function (price) {
            if (price === null || price === undefined || price === '') return true;
            const {originalPrice} = this.parent;
            if (Number(price) < 0) return false;
            if (originalPrice == null) return true;
            return Number(price) <= Number(originalPrice);
        }),
    order: yup.number().nullable(),
    originalPrice: yup.number().nullable()
        .test('upsell-original-price-required', t('upsell-orphan-price-error'), function (originalPrice) {
            const {price} = this.parent;
            const hasPrice = price !== null && price !== undefined && price !== '';
            if (!hasPrice) return true;
            return originalPrice != null;
        }),
})).ensure();

const UpsellSelection = (props) => {
    const {value, onChange, options, currencySymbol, errors, locationId} = props;
    const [fetchedOptions, setFetchedOptions] = useState(null);
    const productOptions = options ?? fetchedOptions;
    const sensors = useSensors(useSensor(PointerSensor));

    useEffect(() => {
        if (!locationId || options) return;
        const loadOptions = async () => {
            try {
                const res = await fetchApi('getUpsellOptions', 'POST', { locationId, type: 'hugim' }, false, true);
                setFetchedOptions((res ?? []).map(item => ({
                    ...item,
                    label: `${item.name} (${Number(item.price).toLocaleString()}${currencySymbol ?? ''})`,
                })));
            } catch (e) {
                console.log('error fetching upsell options');
            }
        };
        loadOptions();
    }, [locationId, currencySymbol, options]);
    const emptyRow = (uuid, order = 0) => ({membership_types_id: null, price: null, order, originalPrice: null, randomKey: uuid});
    const rows = useMemo(() => value?.length ? value : [emptyRow(uuid.v4()), emptyRow(uuid.v4(), 1)], [value]);

    useEffect(() => {
        if (!value?.length) {
            onChange(rows);
            return;
        }
        // Hydrated rows from the server have no randomKey — add one for DnD/React keys
        if (value.some(row => !row.randomKey)) {
            onChange(value.map(row => row.randomKey ? row : {...row, randomKey: uuid.v4()}));
        }
    }, [value?.length]);

    const selectedProductIds = useMemo(() => rows.map(row => row.membership_types_id).filter(id => id != null), [rows]);

    const updateRows = (nextRows) => {
        onChange(nextRows.map((row, index) => ({...row, order: index})));
    };

    const onProductChange = (index, productId) => {
        const product = (productOptions ?? []).find(opt => opt.id === productId);
        updateRows(rows.map((row, i) => {
            if (i !== index) return row;
            return {...row, membership_types_id: productId, originalPrice: product ? Number(product.price) : null, price: productId == null ? null : row.price};
        }));
    };

    const onPriceChange = (index, price) => {
        updateRows(rows.map((row, i) => i === index ? {...row, price: price === '' ? null : price} : row));
    };

    // Only row left → reset empty; otherwise remove and renumber
    const onDelete = (index) => {
        if (rows.length === 1) {
            updateRows([emptyRow(uuid.v4(), 0)]);
            return;
        }
        updateRows(rows.filter((_, i) => i !== index));
    };

    const onAddItem = () => updateRows([...rows, emptyRow(uuid.v4(), rows.length)]);

    const onDragEnd = (event) => {
        const {active, over} = event;
        if (!over || active.id === over.id) return;
        const from = rows.findIndex(row => row.randomKey === active.id);
        const to = rows.findIndex(row => row.randomKey === over.id);
        if (from < 0 || to < 0) return;
        updateRows(arrayMove(rows, from, to));
    };

    const getRowError = (index) => {
        if (!errors) return null;
        if (typeof errors === 'string') return index === 0 ? errors : null;
        const rowError = errors[index];
        if (!rowError) return null;
        return rowError.price || rowError.membership_types_id || (typeof rowError === 'string' ? rowError : null);
    };

    const optionsForRow = (row) => (productOptions ?? []).map(opt => ({
        ...opt,
        disabled: selectedProductIds.includes(opt.id) && opt.id !== row.membership_types_id,
    }));

    return (
        <VStack space={'0.75rem'} flex={1}>
            <DndContext sensors={sensors} onDragEnd={onDragEnd} collisionDetection={closestCenter}>
                <SortableContext items={rows.map(row => row.randomKey)} strategy={verticalListSortingStrategy}>
                    {rows.map((row, index) => (
                        <UpsellRow key={row.randomKey} row={row} index={index} productOptions={optionsForRow(row)}
                                   currencySymbol={currencySymbol} onProductChange={onProductChange}
                                   onPriceChange={onPriceChange} onDelete={onDelete} error={getRowError(index)}/>
                    ))}
                </SortableContext>
            </DndContext>
            <CustomButtonV2 type={'link'} text={t('upsell-add-item')} onClick={onAddItem}
                            wrapperStyle={{alignSelf: 'flex-start'}}
                            textProps={{color: Colors.newDesign.mainBlue}}
                            iconProps={{iconColor: Colors.newDesign.mainBlue}}/>
        </VStack>
    );
};

export default UpsellSelection;
